// TODO: Consolidate with duplicate AnalysisReports component in AdvisorPortal domain
import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Line, Pie, Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  ArcElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import Breadcrumbs from "../../components/shared/Breadcrumbs";
import SubPageLayout from "../../components/shared/SubPageLayout";
import Modal from "../../components/shared/Modal";

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  ArcElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
);

const StatCard = ({ title, value, subtitle, icon, textColor }) => (
  <div className="bg-surface-default dark:bg-surface-default p-6 rounded-lg shadow-md dark:shadow-none">
    <div className="flex items-center justify-between mb-4">
      <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">{title}</h3>
      <span className={`text-2xl ${textColor}`}>{icon}</span>
    </div>
    <p className="text-3xl font-bold text-gray-900 dark:text-gray-100">{value}</p>
    {subtitle && <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">{subtitle}</p>}
  </div>
);

const GlobalStats = ({ stats }) => (
  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
    <StatCard
      title="Total Reports"
      value={stats.totalReports}
      subtitle="Generated this month"
      icon="📊"
      textColor="text-blue-500"
    />
    <StatCard
      title="Popular Template"
      value={stats.popularTemplate}
      subtitle={`Used ${stats.popularTemplateUses} times`}
      icon="⭐"
      textColor="text-yellow-500"
    />
    <StatCard
      title="Client Engagement"
      value={`${stats.clientEngagement}%`}
      subtitle="Report view rate"
      icon="👥"
      textColor="text-green-500"
    />
    <StatCard
      title="Time Saved"
      value={stats.timeSaved}
      subtitle="Hours this month"
      icon="⏱️"
      textColor="text-purple-500"
    />
  </div>
);

const TemplateCard = ({ template, onSelect }) => (
  <motion.div
    className="bg-surface-default dark:bg-surface-default rounded-lg shadow-md dark:shadow-none hover:shadow-lg dark:shadow-none transition-shadow"
    whileHover={{ scale: 1.02 }}
    whileTap={{ scale: 0.98 }}
  >
    <div className="p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-3">
          <span className="text-2xl">{template.icon}</span>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            {template.name}
          </h3>
        </div>
        <span className={`px-3 py-1 rounded-full text-sm ${template.tagColor}`}>
          {template.tag}
        </span>
      </div>
      <p className="text-gray-600 dark:text-gray-400 mb-4">{template.description}</p>
      <div className="space-y-3">
        <div className="flex items-center text-sm text-gray-500 dark:text-gray-500">
          <svg
            className="w-5 h-5 mr-2"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
            />
          </svg>
          {template.components} Components
        </div>
        <div className="flex items-center text-sm text-gray-500 dark:text-gray-500">
          <svg
            className="w-5 h-5 mr-2"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          {template.timeEstimate}
        </div>
      </div>
    </div>
    <div className="border-t px-6 py-4">
      <button
        onClick={() => onSelect(template)}
        className="w-full px-4 py-2 bg-[#202EC6] text-white rounded-lg hover:bg-[#1a24a1] transition-colors"
      >
        Use Template
      </button>
    </div>
  </motion.div>
);

const AIAssistant = ({
  chatMessages,
  chatInput,
  setChatInput,
  handleChatSubmit,
}) => (
  <div className="bg-surface-default dark:bg-surface-default rounded-lg shadow-lg dark:shadow-none p-8 mb-8">
    <div className="max-w-4xl mx-auto">
      <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-6">
        AI Report Assistant
      </h2>
      <div className="h-[400px] overflow-y-auto border rounded-lg mb-6 bg-gray-50 p-6">
        <div className="space-y-6">
          {chatMessages.map((message, index) => (
            <div
              key={index}
              className={`flex ${message.type === "user" ? "justify-end" : "justify-start"}`}
            >
              <div
                className={`max-w-2xl p-4 rounded-xl ${
                  message.type === "user"
                    ? "bg-gray-100 text-gray-900 dark:text-gray-100"
                    : "bg-surface-default dark:bg-surface-default border shadow-sm text-gray-900 dark:text-gray-100"
                }`}
              >
                <div className="flex items-start gap-3">
                  {message.type !== "user" && (
                    <div className="w-8 h-8 rounded-full bg-[#202EC6] flex items-center justify-center flex-shrink-0">
                      <svg
                        className="w-5 h-5 text-white"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"
                        />
                      </svg>
                    </div>
                  )}
                  <div className="flex-1">
                    {message.type !== "user" && (
                      <div className="text-sm text-gray-500 dark:text-gray-500 mb-1">
                        AI Assistant
                      </div>
                    )}
                    <div className="text-gray-900 dark:text-gray-100">{message.content}</div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
      <form onSubmit={handleChatSubmit}>
        <div className="flex gap-4">
          <input
            type="text"
            value={chatInput}
            onChange={(e) => setChatInput(e.target.value)}
            placeholder="Ask for help creating a report..."
            className="flex-1 px-6 py-4 border rounded-lg focus:ring-2 focus:ring-[#202EC6] focus:border-[#202EC6] text-lg text-gray-900 dark:text-gray-100 placeholder-gray-500"
          />
          <button
            type="submit"
            className="px-8 py-4 bg-[#202EC6] text-white rounded-lg hover:bg-[#1a24a1] text-lg font-medium"
          >
            Send
          </button>
        </div>
      </form>
    </div>
  </div>
);

const ClientProfile = ({ client }) => (
  <div className="bg-surface-default dark:bg-surface-default rounded-lg shadow-md dark:shadow-none p-6 mb-6">
    <div className="flex items-center justify-between mb-4">
      <div>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">{client.name}</h3>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Last Updated: {client.lastUpdated}
        </p>
      </div>
      <div className="flex items-center">
        <div className="mr-4">
          <div className="text-sm text-gray-600 dark:text-gray-400">AI Confidence Score</div>
          <div className="text-lg font-semibold text-blue-600">
            {client.aiConfidenceScore}%
          </div>
        </div>
        <div className="h-12 w-12 rounded-full bg-blue-100 flex items-center justify-center">
          <span className="text-xl">👤</span>
        </div>
      </div>
    </div>
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
      <div className="p-3 bg-gray-50 rounded-lg">
        <div className="text-sm text-gray-600 dark:text-gray-400">Age</div>
        <div className="text-lg font-semibold text-gray-900 dark:text-gray-100">{client.age}</div>
      </div>
      <div className="p-3 bg-gray-50 rounded-lg">
        <div className="text-sm text-gray-600 dark:text-gray-400">Life Stage</div>
        <div className="text-lg font-semibold text-gray-900 dark:text-gray-100">
          {client.lifeStage}
        </div>
      </div>
      <div className="p-3 bg-gray-50 rounded-lg">
        <div className="text-sm text-gray-600 dark:text-gray-400">Risk Profile</div>
        <div className="text-lg font-semibold text-gray-900 dark:text-gray-100">
          {client.riskProfile}
        </div>
      </div>
    </div>
    <div className="border-t pt-4">
      <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-2">
        Missing Critical Data
      </h4>
      <div className="flex flex-wrap gap-2">
        {client.missingData.map((item, index) => (
          <span
            key={index}
            className="px-2 py-1 bg-red-100 text-red-800 rounded-full text-xs"
          >
            {item}
          </span>
        ))}
      </div>
    </div>
  </div>
);

const AnalyticsSection = ({ data }) => (
  <div className="bg-surface-default dark:bg-surface-default rounded-lg shadow-md dark:shadow-none p-6 mb-8">
    <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-6">
      Analytics & Insights
    </h2>
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <div>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
          Template Performance
        </h3>
        <div className="h-64">
          <Bar
            data={data.templatePerformance}
            options={{
              responsive: true,
              maintainAspectRatio: false,
              scales: {
                y: {
                  beginAtZero: true,
                  title: {
                    display: true,
                    text: "Usage Count",
                  },
                },
              },
            }}
          />
        </div>
      </div>
      <div>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
          Client Engagement
        </h3>
        <div className="h-64">
          <Line
            data={data.clientEngagement}
            options={{
              responsive: true,
              maintainAspectRatio: false,
              scales: {
                y: {
                  beginAtZero: true,
                  title: {
                    display: true,
                    text: "Engagement Rate (%)",
                  },
                },
              },
            }}
          />
        </div>
      </div>
    </div>
    <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
      {data.insights.map((insight, index) => (
        <div key={index} className="p-4 bg-blue-50 rounded-lg">
          <div className="flex items-start">
            <div className="flex-shrink-0">
              <svg
                className="h-6 w-6 text-blue-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
            <div className="ml-3">
              <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100">
                {insight.title}
              </h4>
              <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                {insight.description}
              </p>
            </div>
          </div>
        </div>
      ))}
    </div>
  </div>
);

const CustomTemplatesSection = ({
  templates,
  onCreateTemplate,
  onEditTemplate,
  onDeleteTemplate,
  onUseTemplate,
}) => {
  const [activeTab, setActiveTab] = useState("personal");

  const filteredTemplates = templates.filter(
    (template) => template.type === activeTab,
  );

  return (
    <div className="bg-surface-default dark:bg-surface-default rounded-lg shadow-md dark:shadow-none p-6 mb-8">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">Custom Templates</h2>
        <button
          onClick={onCreateTemplate}
          className="px-4 py-2 bg-[#202EC6] text-white rounded-lg hover:bg-[#1a24a1] text-sm font-medium"
        >
          Create Template
        </button>
      </div>
      <div className="mb-6">
        <div className="border-b border-gray-200 dark:border-gray-700">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => setActiveTab("personal")}
              className={`whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === "personal"
                  ? "border-[#202EC6] text-[#202EC6]"
                  : "border-transparent text-gray-500 dark:text-gray-500 hover:text-gray-700 dark:text-gray-300 hover:border-gray-300"
              }`}
            >
              Personal Templates
            </button>
            <button
              onClick={() => setActiveTab("firm")}
              className={`whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === "firm"
                  ? "border-[#202EC6] text-[#202EC6]"
                  : "border-transparent text-gray-500 dark:text-gray-500 hover:text-gray-700 dark:text-gray-300 hover:border-gray-300"
              }`}
            >
              Firm Templates
            </button>
          </nav>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {filteredTemplates.map((template) => (
          <div key={template.id} className="border rounded-lg p-4">
            <div className="flex justify-between items-start mb-4">
              <div>
                <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">
                  {template.name}
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                  Created {template.createdAt}
                </p>
              </div>
              <div className="flex space-x-2">
                <button
                  onClick={() => onEditTemplate(template)}
                  className="p-2 text-gray-400 hover:text-gray-500 dark:text-gray-500"
                >
                  <svg
                    className="h-5 w-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                    />
                  </svg>
                </button>
                <button
                  onClick={() => onDeleteTemplate(template)}
                  className="p-2 text-gray-400 hover:text-gray-500 dark:text-gray-500"
                >
                  <svg
                    className="h-5 w-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                    />
                  </svg>
                </button>
              </div>
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">{template.description}</p>
            <div className="flex items-center justify-between">
              <div className="flex items-center text-sm text-gray-500 dark:text-gray-500">
                <svg
                  className="w-5 h-5 mr-2"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                Last used {template.lastUsed}
              </div>
              <button
                onClick={() => onUseTemplate(template)}
                className="px-4 py-2 bg-[#202EC6] text-white rounded-lg hover:bg-[#1a24a1] text-sm font-medium"
              >
                Use Template
              </button>
            </div>
          </div>
        ))}
        {filteredTemplates.length === 0 && (
          <div className="col-span-2 text-center py-8 text-gray-500 dark:text-gray-500">
            No {activeTab === "personal" ? "personal" : "firm"} templates
            available
          </div>
        )}
      </div>
    </div>
  );
};

// Add TabNavigation component
const TabNavigation = ({ activeTab, setActiveTab }) => (
  <div className="border-b border-gray-200 dark:border-gray-700 mb-8">
    <nav className="-mb-px flex space-x-8">
      <button
        onClick={() => setActiveTab("dashboard")}
        className={`py-4 px-1 border-b-2 font-medium text-sm ${
          activeTab === "dashboard"
            ? "border-[#202EC6] text-[#202EC6]"
            : "border-transparent text-gray-500 dark:text-gray-500 hover:text-gray-700 dark:text-gray-300 hover:border-gray-300"
        }`}
      >
        Dashboard
      </button>
      <button
        onClick={() => setActiveTab("templates")}
        className={`py-4 px-1 border-b-2 font-medium text-sm ${
          activeTab === "templates"
            ? "border-[#202EC6] text-[#202EC6]"
            : "border-transparent text-gray-500 dark:text-gray-500 hover:text-gray-700 dark:text-gray-300 hover:border-gray-300"
        }`}
      >
        Templates
      </button>
      <button
        onClick={() => setActiveTab("analytics")}
        className={`py-4 px-1 border-b-2 font-medium text-sm ${
          activeTab === "analytics"
            ? "border-[#202EC6] text-[#202EC6]"
            : "border-transparent text-gray-500 dark:text-gray-500 hover:text-gray-700 dark:text-gray-300 hover:border-gray-300"
        }`}
      >
        Analytics
      </button>
      <button
        onClick={() => setActiveTab("ai-assistant")}
        className={`py-4 px-1 border-b-2 font-medium text-sm ${
          activeTab === "ai-assistant"
            ? "border-[#202EC6] text-[#202EC6]"
            : "border-transparent text-gray-500 dark:text-gray-500 hover:text-gray-700 dark:text-gray-300 hover:border-gray-300"
        }`}
      >
        AI Assistant
      </button>
    </nav>
  </div>
);

const AnalysisReports = () => {
  const navigate = useNavigate();
  const [chatInput, setChatInput] = useState("");
  const [chatMessages, setChatMessages] = useState([
    {
      type: "bot",
      content:
        "Welcome! Would you like help creating a report or finding a template?",
    },
  ]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [showTemplateModal, setShowTemplateModal] = useState(false);

  // Mock data for statistics
  const [stats] = useState({
    totalReports: 128,
    popularTemplate: "Retirement Ready",
    popularTemplateUses: 45,
    clientEngagement: 78,
    timeSaved: 156,
  });

  // Mock data for templates
  const [templates] = useState([
    {
      id: 1,
      name: "Retirement Ready",
      description:
        "Comprehensive retirement analysis including RMD calculations, Social Security optimization, and retirement income projections.",
      icon: "🎯",
      tag: "Most Popular",
      tagColor: "bg-green-100 text-green-800",
      components: 5,
      timeEstimate: "10-15 minutes",
      calculators: ["rmd", "social-security", "retirement-savings"],
    },
    {
      id: 2,
      name: "Tax Strategy",
      description:
        "In-depth tax analysis with Roth conversion opportunities, tax-loss harvesting, and charitable giving strategies.",
      icon: "📋",
      tag: "New",
      tagColor: "bg-blue-100 text-blue-800",
      components: 4,
      timeEstimate: "8-12 minutes",
      calculators: [
        "roth-conversion",
        "tax-loss-harvesting",
        "charitable-contribution",
      ],
    },
    {
      id: 3,
      name: "Investment Review",
      description:
        "Portfolio analysis with risk assessment, rebalancing recommendations, and performance attribution.",
      icon: "📈",
      tag: "Essential",
      tagColor: "bg-purple-100 text-purple-800",
      components: 6,
      timeEstimate: "12-18 minutes",
      calculators: [
        "portfolio-rebalancing",
        "risk-tolerance",
        "compound-interest",
      ],
    },
    {
      id: 4,
      name: "Estate Planning",
      description:
        "Estate planning overview with wealth transfer strategies and legacy planning recommendations.",
      icon: "📝",
      tag: "Complex",
      tagColor: "bg-yellow-100 text-yellow-800",
      components: 5,
      timeEstimate: "15-20 minutes",
      calculators: ["net-worth", "estate-tax", "inheritance-planning"],
    },
  ]);

  // New state variables for enhanced functionality
  const [selectedClient, setSelectedClient] = useState(null);
  const [activeTab, setActiveTab] = useState("personal");
  const [showActionPanel, setShowActionPanel] = useState(true);

  // Mock data for analytics
  const [analyticsData] = useState({
    templatePerformance: {
      labels: [
        "Retirement Ready",
        "Tax Strategy",
        "Investment Review",
        "Estate Planning",
      ],
      datasets: [
        {
          label: "Usage Count",
          data: [45, 32, 28, 20],
          backgroundColor: "#4F46E5",
        },
      ],
    },
    clientEngagement: {
      labels: ["Jan", "Feb", "Mar", "Apr", "May", "Jun"],
      datasets: [
        {
          label: "Engagement Rate",
          data: [65, 70, 75, 78, 82, 85],
          borderColor: "#4F46E5",
          tension: 0.4,
        },
      ],
    },
    insights: [
      {
        title: "Top Performing Template",
        description: "Retirement Ready template shows 25% higher engagement",
      },
      {
        title: "Client Behavior",
        description: "Reports with RMD analysis have 2x more interactions",
      },
      {
        title: "Optimization Tip",
        description:
          "Adding tax planning increases template effectiveness by 15%",
      },
    ],
  });

  // Mock data for custom templates
  const [customTemplates] = useState([
    {
      id: 1,
      name: "High Net Worth Review",
      description: "Comprehensive review template for high net worth clients",
      createdAt: "2024-02-15",
      lastUsed: "2 days ago",
      type: "personal",
    },
    {
      id: 2,
      name: "Annual Strategy Update",
      description: "Template for annual client strategy updates",
      createdAt: "2024-01-20",
      lastUsed: "1 week ago",
      type: "firm",
    },
  ]);

  // Mock data for selected client
  const [clientData] = useState({
    name: "John Smith",
    age: 58,
    lastUpdated: "2024-02-20",
    lifeStage: "Pre-retirement",
    riskProfile: "Moderate",
    aiConfidenceScore: 85,
    missingData: [
      "Social Security Estimate",
      "Healthcare Costs",
      "Legacy Goals",
    ],
  });

  // Add new state for main tab navigation
  const [activeMainTab, setActiveMainTab] = useState("dashboard");

  const handleChatSubmit = (e) => {
    e.preventDefault();
    if (!chatInput.trim()) return;

    // Add user message
    setChatMessages((prev) => [...prev, { type: "user", content: chatInput }]);

    // Simulate AI response
    setTimeout(() => {
      let response =
        "Based on your question, I recommend the following templates: ";
      if (chatInput.toLowerCase().includes("retirement")) {
        response +=
          "Retirement Ready - This template includes RMD calculations and Social Security optimization.";
      } else if (chatInput.toLowerCase().includes("tax")) {
        response +=
          "Tax Strategy - This template provides comprehensive tax planning analysis.";
      } else if (chatInput.toLowerCase().includes("investment")) {
        response +=
          "Investment Review - This template offers portfolio analysis and rebalancing recommendations.";
      } else {
        response +=
          "Would you like me to suggest a template based on your specific needs?";
      }

      setChatMessages((prev) => [...prev, { type: "bot", content: response }]);
    }, 1000);

    setChatInput("");
  };

  const handleTemplateSelect = (template) => {
    setSelectedTemplate(template);
    setShowTemplateModal(true);
  };

  const handleCreateTemplate = () => {
    // Implementation for template creation
  };

  const handleEditTemplate = (template) => {
    // Implementation for template editing
  };

  const handleDeleteTemplate = (template) => {
    // Implementation for template deletion
  };

  const handleExport = () => {
    // Implementation for export functionality
  };

  const renderDashboardTab = () => (
    <>
      <ClientProfile client={clientData} />
      <GlobalStats stats={stats} />
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div>
          <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-6">
            Recommended Templates
          </h2>
          <div className="space-y-4">
            {templates.slice(0, 2).map((template) => (
              <TemplateCard
                key={template.id}
                template={template}
                onSelect={handleTemplateSelect}
              />
            ))}
          </div>
        </div>
        <div>
          <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-6">
            Recent Activity
          </h2>
          <div className="bg-surface-default dark:bg-surface-default rounded-lg shadow-md dark:shadow-none p-6">
            {/* Add recent activity content here */}
          </div>
        </div>
      </div>
    </>
  );

  const renderTemplatesTab = () => (
    <div className="space-y-8">
      <div>
        <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-6">
          Popular Templates
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {templates.map((template) => (
            <TemplateCard
              key={template.id}
              template={template}
              onSelect={handleTemplateSelect}
            />
          ))}
        </div>
      </div>
      <CustomTemplatesSection
        templates={customTemplates}
        onCreateTemplate={handleCreateTemplate}
        onEditTemplate={handleEditTemplate}
        onDeleteTemplate={handleDeleteTemplate}
        onUseTemplate={handleTemplateSelect}
      />
    </div>
  );

  const renderAnalyticsTab = () => <AnalyticsSection data={analyticsData} />;

  const renderAIAssistantTab = () => (
    <div className="max-w-4xl mx-auto">
      <AIAssistant
        chatMessages={chatMessages}
        chatInput={chatInput}
        setChatInput={setChatInput}
        handleChatSubmit={handleChatSubmit}
      />
    </div>
  );

  const renderActiveTab = () => {
    switch (activeMainTab) {
      case "dashboard":
        return renderDashboardTab();
      case "templates":
        return renderTemplatesTab();
      case "analytics":
        return renderAnalyticsTab();
      case "ai-assistant":
        return renderAIAssistantTab();
      default:
        return renderDashboardTab();
    }
  };

  return (
    <SubPageLayout
      breadcrumbItems={[
        { label: "Financial Planning", path: "/financial-planning" },
        {
          label: "Analysis Reports",
          path: "/financial-planning/analysis-reports",
        },
      ]}
      title="Analysis Reports"
      subtitle="Create impactful client deliverables with AI assistance"
    >
      <div className="max-w-7xl mx-auto">
        <TabNavigation
          activeTab={activeMainTab}
          setActiveTab={setActiveMainTab}
        />
        {renderActiveTab()}
      </div>

      {/* Template Modal */}
      <Modal 
        isOpen={showTemplateModal} 
        onClose={() => {
          setShowTemplateModal(false);
          setSelectedTemplate(null);
        }}
        maxWidth="2xl"
      >
        <div className="p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">
              {selectedTemplate
                ? `Use ${selectedTemplate.name} Template`
                : "Create New Report"}
            </h3>
            <button
              onClick={() => {
                setShowTemplateModal(false);
                setSelectedTemplate(null);
              }}
              className="text-gray-400 hover:text-gray-500 dark:text-gray-500"
            >
              <svg
                className="h-6 w-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
        </div>
        <div className="p-6">
          <div className="space-y-6">
            {selectedTemplate ? (
              <>
                <p className="text-gray-600 dark:text-gray-400">
                  {selectedTemplate.description}
                </p>
                <div>
                  <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-2">
                    Included Calculators:
                  </h4>
                  <ul className="space-y-2">
                    {selectedTemplate.calculators.map((calc, index) => (
                      <li
                        key={index}
                        className="flex items-center text-gray-600 dark:text-gray-400"
                      >
                        <svg
                          className="w-5 h-5 mr-2 text-green-500"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M5 13l4 4L19 7"
                          />
                        </svg>
                        {calc}
                      </li>
                    ))}
                  </ul>
                </div>
              </>
            ) : (
              <div className="text-center py-8">
                <p className="text-gray-600 dark:text-gray-400">
                  Select a template to get started
                </p>
              </div>
            )}
          </div>
        </div>
        <div className="p-6 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-neutral-900 flex justify-end gap-4">
          <button
            onClick={() => {
              setShowTemplateModal(false);
              setSelectedTemplate(null);
            }}
            className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100"
          >
            Cancel
          </button>
          <button
            onClick={() => {
              // Handle template selection
              setShowTemplateModal(false);
              setSelectedTemplate(null);
              // Navigate to report creation
              navigate("/report-builder");
            }}
            className="px-6 py-2 bg-[#202EC6] text-white rounded-lg hover:bg-[#1a24a1] text-sm font-medium"
          >
            Continue
          </button>
        </div>
      </Modal>
    </SubPageLayout>
  );
};

export default AnalysisReports;
